import { useEffect, useState } from 'react';
import { isNotFound } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { getSDK } from '../../services/sdk-instance';

const HANDLE_RE =
  /media:\/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})@v(\d+)/gi;

const SVG_NS = 'http://www.w3.org/2000/svg';

/** HTML-tree SVG omits xmlns; `<img>` and file downloads cannot parse without it (media ADR-007). */
export function ensureStandaloneSvg(bytes: ArrayBuffer): ArrayBuffer {
  const text = new TextDecoder().decode(bytes);
  const match = text.match(/<svg[\s>]/i);
  if (!match || match.index === undefined) {
    throw new Error('svg preview is not an svg document');
  }
  const start = match.index;
  const gt = text.indexOf('>', start);
  if (gt === -1) {
    throw new Error('svg preview has no root tag');
  }
  if (/\sxmlns\s*=/i.test(text.slice(start, gt))) {
    return bytes;
  }
  const fixed =
    text.slice(0, start) +
    text.slice(start, gt).replace(/^<svg/i, `<svg xmlns="${SVG_NS}"`) +
    text.slice(gt);
  return new TextEncoder().encode(fixed).buffer;
}

export interface ParsedMediaHandle {
  raw: string;
  id: string;
  version?: number;
}

export function parseMediaHandles(text: string): ParsedMediaHandle[] {
  const found: ParsedMediaHandle[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(HANDLE_RE)) {
    const raw = match[0];
    if (seen.has(raw)) continue;
    seen.add(raw);
    const versionRaw = match[2];
    found.push({
      raw,
      id: match[1],
      version: versionRaw === undefined ? undefined : Number(versionRaw),
    });
  }
  return found;
}

export type MessageDiagramViewState =
  | { kind: 'loading'; width?: number; height?: number }
  | {
      kind: 'ready';
      width: number;
      height: number;
      previewUrl: string;
      previewKind: 'svg' | 'png';
      warnings: string[];
      label: string;
    }
  | { kind: 'failed'; message: string }
  | { kind: 'tombstone' };

export interface MessageDiagramViewProps {
  state: MessageDiagramViewState;
  pinnedVersion?: number;
  downloadError?: string;
  onDownloadLatest?: () => void;
  onDownloadPinned?: () => void;
  onPreviewError?: () => void;
}

export function MessageDiagramView({
  state,
  pinnedVersion,
  downloadError,
  onDownloadLatest,
  onDownloadPinned,
  onPreviewError,
}: MessageDiagramViewProps) {
  const reserved =
    (state.kind === 'loading' || state.kind === 'ready') &&
    state.width !== undefined &&
    state.height !== undefined
      ? { width: state.width, height: state.height }
      : undefined;

  return (
    <figure
      data-testid="message-diagram"
      data-diagram-state={state.kind}
      data-diagram-version={pinnedVersion === undefined ? undefined : String(pinnedVersion)}
      data-has-warnings={state.kind === 'ready' && state.warnings.length > 0 ? 'true' : 'false'}
      data-preview-kind={state.kind === 'ready' ? state.previewKind : undefined}
      className="mt-3 max-w-full overflow-hidden rounded-md bg-card"
    >
      {state.kind === 'tombstone' ? (
        <div
          data-testid="message-diagram-tombstone"
          className="px-3 py-4 text-sm text-muted-foreground"
        >
          This artifact is in Trash or has been deleted.
        </div>
      ) : null}

      {state.kind === 'failed' ? (
        <div
          data-testid="message-diagram-failed"
          className="px-3 py-4 text-sm text-destructive"
          role="alert"
        >
          {state.message}
        </div>
      ) : null}

      {state.kind === 'loading' ? (
        <div
          data-testid="message-diagram-loading"
          className="bg-transparent"
          style={reserved}
          aria-busy="true"
        />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {state.warnings.length > 0 ? (
            <div
              data-testid="message-diagram-warning"
              className="px-3 py-2 text-xs text-muted-foreground"
            >
              This drawing contains interactivity that will not run here:{' '}
              {state.warnings.join(', ')}. Download the file to open it elsewhere.
            </div>
          ) : null}
          <img
            data-testid="message-diagram-preview"
            src={state.previewUrl}
            alt={state.label}
            width={state.width}
            height={state.height}
            className="block max-w-full bg-transparent"
            style={{ filter: 'none' }}
            onError={onPreviewError}
          />
        </>
      ) : null}

      <figcaption className="flex flex-wrap items-center gap-2 px-3 py-2">
        {onDownloadLatest ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="message-diagram-download-latest"
            onClick={onDownloadLatest}
          >
            Download latest
          </Button>
        ) : null}
        {onDownloadPinned && pinnedVersion !== undefined ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="message-diagram-download-pinned"
            onClick={onDownloadPinned}
          >
            Download v{pinnedVersion}
          </Button>
        ) : null}
        {downloadError ? (
          <span data-testid="message-diagram-download-error" className="text-xs text-destructive">
            {downloadError}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}

function filenameFor(label: string, suffix: string): string {
  const stem = label.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  if (stem.length === 0) {
    throw new Error('artifact label produced an empty download name');
  }
  return `${stem}-${suffix}`;
}

function triggerDownload(bytes: ArrayBuffer, filename: string, mime: string): void {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function MessageDiagram({ handle }: { handle: ParsedMediaHandle }) {
  const [state, setState] = useState<MessageDiagramViewState>({ kind: 'loading' });
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  const [label, setLabel] = useState('diagram');

  useEffect(() => {
    let cancelled = false;
    let previewUrl: string | undefined;

    const load = async () => {
      try {
        const sdk = getSDK();
        const object = await sdk.media.get(handle.id);
        if (cancelled) return;
        if (object.deleted_at) {
          setState({ kind: 'tombstone' });
          return;
        }
        const versions = object.versions;
        if (!versions || versions.length === 0) {
          throw new Error('media object has no versions');
        }
        if (handle.version === undefined) {
          throw new Error('chat media handle omitted a version');
        }
        const pinned = handle.version;
        const version = versions.find((row) => row.version === pinned);
        if (!version) {
          throw new Error(`media object has no version ${pinned}`);
        }
        if (version.width === undefined || version.height === undefined) {
          throw new Error(`media version ${pinned} is missing width or height`);
        }
        setLabel(object.label);
        setState({ kind: 'loading', width: version.width, height: version.height });
        const warned = version.warnings.length > 0;
        const format = warned ? 'png' : 'svg';
        const raw = await sdk.media.content(handle.id, { format, version: pinned });
        if (cancelled) return;
        const bytes = format === 'svg' ? ensureStandaloneSvg(raw) : raw;
        const blob = new Blob([bytes], { type: warned ? 'image/png' : 'image/svg+xml' });
        previewUrl = URL.createObjectURL(blob);
        setState({
          kind: 'ready',
          width: version.width,
          height: version.height,
          previewUrl,
          previewKind: format,
          warnings: version.warnings,
          label: object.label,
        });
      } catch (error) {
        if (cancelled) return;
        if (isNotFound(error)) {
          setState({ kind: 'tombstone' });
          return;
        }
        const message = error instanceof Error ? error.message : String(error);
        setState({ kind: 'failed', message });
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [handle.id, handle.version]);

  const download = async (version: number | undefined, suffix: string) => {
    setDownloadError(undefined);
    try {
      const raw = await getSDK().media.content(handle.id, { format: 'svg', version });
      const bytes = ensureStandaloneSvg(raw);
      triggerDownload(bytes, filenameFor(label, suffix), 'image/svg+xml');
    } catch (error) {
      if (isNotFound(error)) {
        setDownloadError('This artifact is in Trash or has been deleted.');
        return;
      }
      setDownloadError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <MessageDiagramView
      state={state}
      pinnedVersion={handle.version}
      downloadError={downloadError}
      onPreviewError={() => {
        setState({ kind: 'failed', message: 'diagram preview could not be displayed' });
      }}
      onDownloadLatest={() => {
        void download(undefined, 'latest.svg');
      }}
      onDownloadPinned={
        handle.version === undefined
          ? undefined
          : () => {
              void download(handle.version, `v${handle.version}.svg`);
            }
      }
    />
  );
}

export function MessageDiagrams({ text }: { text: string }) {
  const handles = parseMediaHandles(text);
  if (handles.length === 0) return null;
  return (
    <div data-testid="message-diagrams" className="flex flex-col gap-2">
      {handles.map((handle) => (
        <MessageDiagram key={handle.raw} handle={handle} />
      ))}
    </div>
  );
}
